import { useState, useRef, useEffect } from 'react'
import { projectsApi } from '../lib/api'
import type { Project } from '../types'

interface ProjectSelectorProps {
  projects: Project[]
  selectedProject: Project | null
  onSelect: (project: Project | null) => void
  onProjectCreated?: (project: Project) => void
  isLoading?: boolean
}

export function ProjectSelector({
  projects,
  selectedProject,
  onSelect,
  onProjectCreated,
  isLoading = false,
}: ProjectSelectorProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [createError, setCreateError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setIsCreating(false)
        setNewProjectName('')
        setCreateError(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (isCreating) {
          setIsCreating(false)
          setNewProjectName('')
          setCreateError(null)
        } else {
          setIsOpen(false)
        }
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isCreating])

  // Focus input when entering create mode
  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isCreating])

  // Handle creating a new project
  const handleCreateProject = async () => {
    if (!newProjectName.trim()) {
      setCreateError('Please enter a project name')
      return
    }

    setIsSubmitting(true)
    setCreateError(null)

    try {
      const newProject = await projectsApi.create({ name: newProjectName.trim() })
      onProjectCreated?.(newProject)
      onSelect(newProject)
      setNewProjectName('')
      setIsCreating(false)
      setIsOpen(false)
    } catch (err: any) {
      setCreateError(err.message || 'Failed to create project')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Trigger */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={isLoading}
        className={`w-full h-12 px-4 rounded-lg border bg-white flex items-center justify-between gap-2 transition-all ${
          isOpen
            ? 'border-primary ring-2 ring-primary/20'
            : 'border-border-color hover:border-primary/50'
        } ${isLoading ? 'opacity-60 cursor-not-allowed' : ''}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="material-symbols-outlined text-lg text-text-muted">
            {selectedProject ? 'folder' : 'folder_off'}
          </span>
          <span className={`text-sm truncate ${selectedProject ? 'text-text-main font-medium' : 'text-text-muted'}`}>
            {isLoading
              ? 'Loading projects...'
              : selectedProject
              ? selectedProject.name
              : 'No Project (Save to Gallery)'}
          </span>
        </div>
        <span
          className={`material-symbols-outlined text-lg text-text-muted transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          expand_more
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && !isLoading && (
        <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white rounded-lg border border-border-color shadow-lg overflow-hidden animate-fade-up">
          {/* No Project Option */}
          <button
            onClick={() => {
              onSelect(null)
              setIsOpen(false)
            }}
            className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-soft transition-colors ${
              !selectedProject ? 'bg-primary/5' : ''
            }`}
          >
            <span className="material-symbols-outlined text-lg text-text-muted">
              folder_off
            </span>
            <div className="flex-1 text-left">
              <span className="text-sm text-text-main">No Project</span>
              <span className="text-xs text-text-muted block">Save directly to Gallery</span>
            </div>
            {!selectedProject && (
              <span className="material-symbols-outlined text-lg text-primary">check</span>
            )}
          </button>

          {/* Divider */}
          {projects.length > 0 && <div className="border-t border-border-color" />}

          {/* Project List */}
          <div className="max-h-48 overflow-y-auto">
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => {
                  onSelect(project)
                  setIsOpen(false)
                }}
                className={`w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-soft transition-colors ${
                  selectedProject?.id === project.id ? 'bg-primary/5' : ''
                }`}
              >
                {/* Project Thumbnail */}
                {project.coverImage ? (
                  <div className="w-8 h-8 rounded overflow-hidden flex-shrink-0 bg-surface-muted">
                    <img
                      src={project.coverImage}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ) : (
                  <div className="w-8 h-8 rounded bg-surface-muted flex items-center justify-center flex-shrink-0">
                    <span className="material-symbols-outlined text-sm text-text-muted">folder</span>
                  </div>
                )}

                {/* Project Info */}
                <div className="flex-1 text-left min-w-0">
                  <span className="text-sm text-text-main font-medium truncate block">
                    {project.name}
                  </span>
                  <span className="text-xs text-text-muted">
                    {project.imageCount} {project.imageCount === 1 ? 'image' : 'images'}
                  </span>
                </div>

                {/* Selected Indicator */}
                {selectedProject?.id === project.id && (
                  <span className="material-symbols-outlined text-lg text-primary">check</span>
                )}
              </button>
            ))}
          </div>

          {/* Create New Project Section */}
          <div className="border-t border-border-color">
            {isCreating ? (
              <div className="p-3">
                <div className="flex items-center gap-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={newProjectName}
                    onChange={(e) => {
                      setNewProjectName(e.target.value)
                      setCreateError(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !isSubmitting) {
                        handleCreateProject()
                      }
                    }}
                    placeholder="Project name..."
                    disabled={isSubmitting}
                    className="flex-1 bg-white border border-border-color rounded-lg px-3 py-2 text-sm text-text-main placeholder:text-gray-400 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none disabled:opacity-60"
                  />
                  <button
                    onClick={handleCreateProject}
                    disabled={isSubmitting || !newProjectName.trim()}
                    className="p-2 rounded-lg bg-primary text-white hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="material-symbols-outlined text-lg animate-spin">progress_activity</span>
                    ) : (
                      <span className="material-symbols-outlined text-lg">check</span>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setIsCreating(false)
                      setNewProjectName('')
                      setCreateError(null)
                    }}
                    disabled={isSubmitting}
                    className="p-2 rounded-lg text-text-muted hover:bg-surface-muted transition-colors disabled:opacity-60"
                  >
                    <span className="material-symbols-outlined text-lg">close</span>
                  </button>
                </div>
                {createError && (
                  <p className="text-xs text-red-600 mt-2 ml-1">{createError}</p>
                )}
              </div>
            ) : (
              <button
                onClick={() => setIsCreating(true)}
                className="w-full px-4 py-3 flex items-center gap-3 hover:bg-surface-soft transition-colors text-primary"
              >
                <span className="material-symbols-outlined text-lg">add</span>
                <span className="text-sm font-medium">Create New Project</span>
              </button>
            )}
          </div>

          {/* Empty State */}
          {projects.length === 0 && !isCreating && (
            <div className="px-4 py-4 text-center text-text-muted border-t border-border-color">
              <span className="material-symbols-outlined text-2xl mb-1 block opacity-50">folder_open</span>
              <p className="text-xs">No projects yet. Create one above!</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
